import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useAuth } from '@/auth/AuthProvider';
import { useProviders, useActivateService } from '@/api/service-provisioning';
import { trackFeatureAction } from '@/lib/analytics-events';
import { CDN_PROVIDER, CDN_PRODUCT, cdnConsoleRoute } from './cdn-activate';

interface CdnActivateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Activate Multi-CDN. A first-time activation defaults to the vEdge orchestrator
 * (`raven` provider): the backend resolves the customer org from the session,
 * mints a fresh vEdge Org + org_admin user, auto-binds the default zone in one
 * call, then drops the customer into the new orchestrator console.
 */
export function CdnActivateDialog({ open, onOpenChange }: CdnActivateDialogProps) {
  const navigate = useNavigate();
  const { show } = useToast();
  const { user } = useAuth();
  const { data: providers = [], isLoading: providersLoading } = useProviders();

  // The default Multi-CDN provider is `raven` (vEdge); only an active provider
  // can be provisioned self-serve.
  const raven = useMemo(
    () =>
      providers.find(
        (p) => p.name?.trim().toLowerCase() === CDN_PROVIDER && p.status === 'active',
      ),
    [providers],
  );

  const activate = useActivateService();
  const [serviceName, setServiceName] = useState<string>(CDN_PRODUCT.displayName);

  const email = user?.email ?? '';
  const canSubmit = !!raven && !!serviceName.trim() && !!email && !activate.isPending;

  function handleSubmit() {
    if (!raven || !canSubmit) return;
    activate.mutate(
      { serviceName: serviceName.trim(), providerId: raven.id, email },
      {
        onSuccess: (service) => {
          trackFeatureAction('cdn', 'provider', 'create');
          show({ tone: 'success', title: 'Multi-CDN activated', description: serviceName.trim() });
          onOpenChange(false);
          navigate(cdnConsoleRoute(service.id));
        },
        onError: (err) => {
          show({
            tone: 'danger',
            title: 'Activation failed',
            description: err.message || 'Could not activate Multi-CDN. Please try again.',
          });
        },
      },
    );
  }

  const unavailable = !providersLoading && !raven;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Activate {CDN_PRODUCT.displayName}</DialogTitle>
          <DialogDescription>{CDN_PRODUCT.description}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {providersLoading ? (
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              <Skeleton style={{ height: 64 }} />
            </div>
          ) : unavailable ? (
            <p style={{ color: 'var(--vn-fg-muted)', margin: 0 }}>
              {CDN_PRODUCT.displayName} is not available for self-serve activation on your account
              yet. Please contact sales to enable it.
            </p>
          ) : (
            <form
              id="cdn-activate-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              style={{ display: 'grid', gap: 'var(--vn-space-4)' }}
            >
              <Field
                label="Service name"
                description="A region and delivery edge are provisioned for you automatically."
              >
                {(props) => (
                  <Input
                    {...props}
                    value={serviceName}
                    onChange={(e) => setServiceName(e.target.value)}
                    placeholder={CDN_PRODUCT.displayName}
                  />
                )}
              </Field>
            </form>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {!unavailable && (
            <Button
              type="submit"
              form="cdn-activate-form"
              disabled={!canSubmit}
              startIcon={<Globe size={16} />}
            >
              {activate.isPending ? 'Activating…' : 'Activate'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
